/**
 * QA Panel — OverlayContainer (phone / touch).
 *
 * On a phone, window.open lands in a separate browser tab, and navigating away
 * from the lobby ends the QA tester's session. So on touch devices we render
 * the panel as a fixed full-viewport iframe mounted on the lobby document — no
 * navigation, session preserved.
 *
 * THE LOAD-BEARING DETAIL — origin / localStorage:
 *   The iframe is mounted with NO `src` and the HTML is written via
 *   contentDocument.open()/write(html)/close() — the exact analog of the
 *   desktop popup.document.write. This inherits the lobby's origin, so the
 *   template's localStorage (theme, jira-verified, autosave) keeps working.
 *   Do NOT switch this to `srcdoc` — a srcdoc iframe has an opaque origin and
 *   localStorage access throws SecurityError, silently killing persistence.
 *
 * Lifecycle: an iframe has no OS window chrome, so OverlayContainer renders its
 * own close affordance (a button over the iframe, plus a backdrop tap target)
 * and close() simply removes the host element from the DOM. There is no
 * external-close path, so no heartbeat poll is needed.
 *
 * postMessage target is `iframe.contentWindow`. Namespace auth means origin is
 * irrelevant and the bridge's `'*'` target works unchanged.
 */

import type { PanelContainer } from './container';

const HOST_ID = 'react-lobby-qa-panel-overlay';

/** Match floatingButton's z-index so the overlay sits above all lobby content. */
const Z_INDEX = '2147483647';

export class OverlayContainer implements PanelContainer {
    private host: HTMLDivElement | null = null;
    private iframe: HTMLIFrameElement | null = null;
    private onCloseRequested: () => void;

    constructor(onCloseRequested: () => void) {
        this.onCloseRequested = onCloseRequested;
    }

    open(html: string): boolean {
        if (typeof document === 'undefined') return false;
        if (this.host) {
            // Already mounted — re-focus is a no-op, treat as success.
            return true;
        }

        const host = document.createElement('div');
        host.id = HOST_ID;
        // position:fixed + inset:0 covers the viewport. overflow:hidden guards
        // against the iframe contributing to document scroll overflow (the same
        // DevTools-zoom feedback loop documented for the Figma overlay root).
        host.setAttribute(
            'style',
            [
                'position: fixed',
                'inset: 0',
                'z-index: ' + Z_INDEX,
                'overflow: hidden',
                // A dim backdrop also doubles as a tap-to-close target.
                'background: rgba(0, 0, 0, 0.4)',
            ].join('; '),
        );

        // Backdrop: tapping outside the iframe closes the overlay. The iframe
        // and the close button stop propagation so taps inside don't close it.
        host.addEventListener('pointerdown', (event) => {
            if (event.target === host) {
                this.onCloseRequested();
            }
        });

        const iframe = document.createElement('iframe');
        iframe.setAttribute(
            'style',
            [
                'position: absolute',
                'inset: 0',
                'width: 100%',
                'height: 100%',
                'border: none',
                'background: #23272F',
            ].join('; '),
        );
        // No src — written via contentDocument below to inherit the lobby origin.
        host.appendChild(iframe);

        const closeBtn = document.createElement('button');
        closeBtn.type = 'button';
        closeBtn.setAttribute('aria-label', 'Close QA panel');
        closeBtn.textContent = '✕'; // ✕
        closeBtn.setAttribute(
            'style',
            [
                'position: absolute',
                'top: max(8px, env(safe-area-inset-top))',
                'right: max(8px, env(safe-area-inset-right))',
                'width: 40px',
                'height: 40px',
                'padding: 0',
                'border: none',
                'border-radius: 50%',
                'background: rgba(0, 0, 0, 0.55)',
                'color: #ffffff',
                'font: 600 18px/1 -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                'cursor: pointer',
                'z-index: 1',
                'display: flex',
                'align-items: center',
                'justify-content: center',
            ].join('; '),
        );
        closeBtn.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.onCloseRequested();
        });
        host.appendChild(closeBtn);

        document.body.appendChild(host);

        const doc = iframe.contentDocument;
        const win = iframe.contentWindow;
        if (!doc || !win) {
            // Mounting failed unexpectedly — clean up and report failure.
            host.remove();
            return false;
        }

        // Same-origin document.write — inherits the lobby origin so the
        // template's localStorage access works. NOT srcdoc.
        doc.open();
        doc.write(html);
        doc.close();

        this.host = host;
        this.iframe = iframe;
        return true;
    }

    target(): Window | null {
        return this.iframe?.contentWindow ?? null;
    }

    isOpen(): boolean {
        return this.host !== null && this.host.isConnected;
    }

    close(): void {
        if (this.host?.isConnected) {
            this.host.remove();
        }
        this.host = null;
        this.iframe = null;
    }

    focus(): void {
        // No OS window to raise; the overlay is already on top via z-index.
        this.iframe?.contentWindow?.focus();
    }
}
